import { useEffect, useState } from 'react';
import { Proposta } from '../../services/propostaService';
import api from '../../services/api';
import { formatDateFromDB } from '../../utils/dateUtils';

interface PropostaVisualizacaoProps {
  proposta: Proposta;
}

interface EmpresaData {
  nome: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  logoEmpresaClaro?: string;
  logoEmpresaEscuro?: string;
  logo?: string;
}

const formatNumero = (numero: number, dataEmissao: string) => {
  const num = String(numero).padStart(3, '0');
  const data = new Date(dataEmissao);
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  return `${num}-${mes}-${ano}`;
};

export function PropostaVisualizacao({ proposta }: PropostaVisualizacaoProps) {
  const [empresa, setEmpresa] = useState<EmpresaData | null>(null);

  useEffect(() => {
    api.get('/admin/empresa')
      .then((res) => setEmpresa(res.data))
      .catch(() => {});
  }, []);

  const empresaNome = empresa?.nome || 'Empresa';
  const clienteNome = proposta.client.nomeFantasia || proposta.client.razaoSocial;
  const clienteCnpj = proposta.client.cnpj || '';
  const logoUrl = empresa?.logoEmpresaClaro || empresa?.logo;

  const endereco = [
    empresa?.logradouro ? `${empresa.logradouro}${empresa.numero ? ', ' + empresa.numero : ''}` : null,
    empresa?.cidade && empresa?.estado ? `${empresa.cidade} – ${empresa.estado}` : empresa?.cidade || empresa?.estado,
    empresa?.cep ? `CEP ${empresa.cep}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <div className="proposta-visualizacao bg-white text-slate-800 px-6 md:px-12">
      {/* Capa */}
      <div className="border-b border-slate-200 pb-8 mb-8">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={empresaNome} className="h-16 object-contain mb-3" />
            ) : (
              <h1 className="text-xl font-bold text-slate-900">{empresaNome}</h1>
            )}
            <div className="text-xs text-slate-500">
              {empresa?.cnpj ? `CNPJ ${empresa.cnpj}` : ''}
              {endereco ? <div className="mt-0.5">{endereco}</div> : ''}
              {empresa?.email || empresa?.telefone ? (
                <div className="mt-0.5">{[empresa?.email, empresa?.telefone].filter(Boolean).join(' · ')}</div>
              ) : null}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-widest text-slate-400">Proposta Comercial</div>
            <div className="text-2xl font-bold text-slate-900">#{formatNumero(proposta.numero, proposta.dataEmissao)}</div>
            <div className="text-xs text-slate-500 mt-1">Emissão: {formatDateFromDB(proposta.dataEmissao)}</div>
            <div className="text-xs text-slate-500">Válida até: {formatDateFromDB(proposta.dataValidade)}</div>
          </div>
        </div>
      </div>

      {/* Cliente */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">Cliente</h2>
        <div className="text-lg font-semibold text-slate-900">{clienteNome}</div>
        {proposta.client.razaoSocial !== clienteNome && (
          <div className="text-sm text-slate-600">{proposta.client.razaoSocial}</div>
        )}
        {clienteCnpj && <div className="text-sm text-slate-500">CNPJ {clienteCnpj}</div>}
      </div>

      {/* Assunto */}
      {proposta.titulo && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">Assunto</h2>
          <div className="text-base font-semibold text-slate-900">{proposta.titulo}</div>
        </div>
      )}

      {/* Conteúdo (escopo gerado por IA) */}
      {proposta.conteudo ? (
        <div
          className="proposta-conteudo"
          dangerouslySetInnerHTML={{ __html: proposta.conteudo }}
        />
      ) : (
        <p className="text-slate-400 text-sm">Nenhum conteúdo definido para esta proposta.</p>
      )}

      {/* Fechamento */}
      <div className="mt-12 pt-8 border-t border-slate-200">
        <div className="grid grid-cols-2 gap-12 mb-10">
          <div className="text-center">
            <div className="border-b border-slate-300 pb-4 mb-4 h-12" />
            <div className="font-semibold text-slate-900">{empresaNome}</div>
            <div className="text-xs text-slate-500">Fornecedor</div>
          </div>
          <div className="text-center">
            <div className="border-b border-slate-300 pb-4 mb-4 h-12" />
            <div className="font-semibold text-slate-900">{proposta.client.razaoSocial}</div>
            <div className="text-xs text-slate-500">Cliente</div>
          </div>
        </div>
        <p className="text-xs text-slate-400 text-center">
          Proposta válida até {formatDateFromDB(proposta.dataValidade)} · Documento gerado em {formatDateFromDB(new Date().toISOString())}
        </p>
      </div>

      <style>{`
        .proposta-conteudo { font-size: 14px; line-height: 1.75; color: #334155; }
        .proposta-conteudo h1 { font-size: 1.4rem; font-weight: 700; color: #0f172a; margin: 2.5rem 0 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #0e7490; }
        .proposta-conteudo h1:first-child { margin-top: 0; }
        .proposta-conteudo h2 { font-size: 1.15rem; font-weight: 700; color: #0f172a; margin: 2rem 0 0.75rem; padding-bottom: 0.4rem; border-bottom: 1px solid #e2e8f0; }
        .proposta-conteudo h3 { font-size: 1rem; font-weight: 600; color: #0e7490; margin: 1.25rem 0 0.5rem; }
        .proposta-conteudo h4 { font-size: 0.95rem; font-weight: 600; color: #0f172a; margin: 1rem 0 0.4rem; }
        .proposta-conteudo p { margin: 0.6rem 0; }
        .proposta-conteudo ul, .proposta-conteudo ol { margin: 0.6rem 0 0.6rem 1.5rem; padding: 0; }
        .proposta-conteudo li { margin: 0.35rem 0; }
        .proposta-conteudo hr { border: none; border-top: 1px solid #e2e8f0; margin: 2rem 0; }
        .proposta-conteudo blockquote { border-left: 4px solid #0e7490; margin: 1rem 0; padding: 0.5rem 1rem; background: #f8fafc; color: #475569; }
        .proposta-conteudo table { width: 100%; border-collapse: collapse; margin: 1.25rem 0; font-size: 0.9rem; }
        .proposta-conteudo table th { background: #0f172a; color: #ffffff; border: 1px solid #0f172a; padding: 0.5rem 0.75rem; text-align: left; font-weight: 600; }
        .proposta-conteudo table td { border: 1px solid #e2e8f0; padding: 0.5rem 0.75rem; text-align: left; }
        .proposta-conteudo table tr:nth-child(even) td { background: #f8fafc; }
        .proposta-conteudo pre { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.75rem 1rem; font-size: 0.85rem; overflow-x: auto; margin: 1rem 0; }
        .proposta-conteudo code { font-family: monospace; font-size: 0.85em; }
        .proposta-conteudo strong { color: #0f172a; }
        .proposta-conteudo h1.parte { background: #0f172a; color: #ffffff; padding: 0.75rem 1rem; border: none; font-size: 1.05rem; letter-spacing: 0.5px; margin: 2.5rem 0 1rem; }
      `}</style>
    </div>
  );
}

export default PropostaVisualizacao;
